import { CookiePreferences } from '../types/cookieConsent';

const COOKIE_STORAGE_KEY = 'varna_imoti_cookie_consent_v2';
const CURRENT_POLICY_VERSION = '2026.1';
let activeTrackingConfig: any = null;

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}

/**
 * Initializes the default Google Consent Mode v2 signals.
 * Should be called early on app mount.
 */
export function initConsentMode(): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      window.dataLayer?.push(arguments);
    };
  }

  const stored = getStoredConsent();
  if (stored) {
    applyConsentSignals(stored);
  } else {
    // European default (Strict Privacy / GDPR): all non-essential denied until explicit consent
    window.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied',
      security_storage: 'granted',
      wait_for_update: 500
    });
  }
}

/**
 * Retrieves the stored consent from localStorage if valid.
 */
export function getStoredConsent(): CookiePreferences | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COOKIE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookiePreferences;
    if (parsed && typeof parsed.necessary === 'boolean' && parsed.version === CURRENT_POLICY_VERSION) {
      return parsed;
    }
  } catch (e) {
    console.warn('Failed reading cookie consent preferences:', e);
  }
  return null;
}

/**
 * Updates Google Consent Mode v2 and Meta Pixel consent according to user choices.
 */
export function applyConsentSignals(prefs: CookiePreferences): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      window.dataLayer?.push(arguments);
    };
  }

  // 1. Google Consent Mode v2 (for Google Analytics 4, Google Ads, Google Search Console)
  window.gtag('consent', 'update', {
    analytics_storage: prefs.analytics ? 'granted' : 'denied',
    ad_storage: prefs.marketing ? 'granted' : 'denied',
    ad_user_data: prefs.marketing ? 'granted' : 'denied',
    ad_personalization: prefs.marketing ? 'granted' : 'denied',
    functionality_storage: prefs.functional ? 'granted' : 'denied',
    personalization_storage: prefs.functional ? 'granted' : 'denied',
    security_storage: 'granted'
  });

  // 2. Meta Pixel (Facebook / Instagram Pixel) Consent
  if (prefs.marketing) {
    if (activeTrackingConfig) {
      syncTrackingScripts(activeTrackingConfig);
    }
    if (typeof window.fbq === 'function') {
      window.fbq('consent', 'grant');
      window.fbq('track', 'PageView');
    }
  } else {
    if (typeof window.fbq === 'function') {
      window.fbq('consent', 'revoke');
    }
  }

  // 3. Dispatch browser event so components (like Google Maps) can react dynamically
  window.dispatchEvent(new CustomEvent('cookie_consent_updated', { detail: prefs }));
}

/**
 * Saves user choices and applies signals.
 */
export function saveConsent(preferences: Omit<CookiePreferences, 'timestamp' | 'version'>): CookiePreferences {
  const fullPrefs: CookiePreferences = {
    necessary: true, // Always true under GDPR
    analytics: Boolean(preferences.analytics),
    marketing: Boolean(preferences.marketing),
    functional: Boolean(preferences.functional),
    timestamp: Date.now(),
    version: CURRENT_POLICY_VERSION
  };

  try {
    localStorage.setItem(COOKIE_STORAGE_KEY, JSON.stringify(fullPrefs));
  } catch (e) {
    console.warn('Could not save cookie preferences to localStorage:', e);
  }

  applyConsentSignals(fullPrefs);
  return fullPrefs;
}

/**
 * Quick helper to accept all categories.
 */
export function acceptAllCookies(): CookiePreferences {
  return saveConsent({
    necessary: true,
    analytics: true,
    marketing: true,
    functional: true
  });
}

/**
 * Quick helper to reject all optional categories (only necessary remains).
 */
export function rejectAllCookies(): CookiePreferences {
  return saveConsent({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });
}

/**
 * Synchronizes tracking scripts (Google Analytics, Google Ads, Meta Pixel, Google Search Console)
 * strictly in accordance with European GDPR / ePrivacy and Consent Mode v2.
 */
export function syncTrackingScripts(tracking?: {
  googleAnalyticsId?: string;
  googleAdsId?: string;
  metaPixelId?: string;
  googleSearchConsoleVerification?: string;
}): void {
  if (typeof window === 'undefined') return;
  activeTrackingConfig = tracking;

  // 1. Google Search Console Verification Meta Tag (strictly necessary / non-tracking verification)
  if (tracking?.googleSearchConsoleVerification) {
    let tag = document.querySelector('meta[name="google-site-verification"]') as HTMLMetaElement | null;
    if (!tag) {
      tag = document.createElement('meta');
      tag.name = 'google-site-verification';
      document.head.appendChild(tag);
    }
    tag.content = tracking.googleSearchConsoleVerification;
  }

  const prefs = getStoredConsent();

  // 2. Google Analytics 4 (GA4)
  if (tracking?.googleAnalyticsId) {
    const gaId = tracking.googleAnalyticsId.trim();
    if (gaId && !document.getElementById('ga4-script')) {
      const script = document.createElement('script');
      script.id = 'ga4-script';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      if (!window.gtag) {
        window.gtag = function () {
          window.dataLayer?.push(arguments);
        };
      }
      window.gtag('js', new Date());
      window.gtag('config', gaId, {
        anonymize_ip: true,
        send_page_view: prefs ? prefs.analytics : false
      });
    }
  }

  // 3. Google Ads Tag
  if (tracking?.googleAdsId) {
    const adsId = tracking.googleAdsId.trim();
    if (adsId) {
      window.dataLayer = window.dataLayer || [];
      if (!window.gtag) {
        window.gtag = function () {
          window.dataLayer?.push(arguments);
        };
      }
      window.gtag('config', adsId);
    }
  }

  // 4. Meta Pixel (Facebook / Instagram) - ONLY loads if marketing consent is granted!
  // This eliminates third-party cookies (c_user, fr, dpr, xs) on audits and unconsented visits.
  if (tracking?.metaPixelId) {
    const pixelId = tracking.metaPixelId.trim();
    if (pixelId && prefs && prefs.marketing) {
      if (!document.getElementById('meta-pixel-script')) {
        /* eslint-disable */
        (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
          if (f.fbq) return;
          n = f.fbq = function() {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = !0;
          n.version = '2.0';
          n.queue = [];
          t = b.createElement(e);
          t.async = !0;
          t.id = 'meta-pixel-script';
          t.src = v;
          s = b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t, s);
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
        /* eslint-enable */
      }

      // European compliance: inform Meta Pixel of user's marketing consent state
      if (typeof window.fbq === 'function') {
        window.fbq('set', 'autoConfig', false, pixelId);
        window.fbq('consent', 'grant');
        window.fbq('init', pixelId);
        window.fbq('track', 'PageView');
      }
    }
  }
}

/**
 * Safe helper to track custom Meta Pixel events (e.g. Lead, ViewContent)
 * Respects user's GDPR marketing consent.
 */
export function trackPixelEvent(eventName: string, params?: Record<string, any>): void {
  if (typeof window === 'undefined') return;
  const prefs = getStoredConsent();
  if (prefs && !prefs.marketing) return; // GDPR guard
  if (typeof window.fbq === 'function') {
    try {
      if (params) {
        window.fbq('track', eventName, params);
      } else {
        window.fbq('track', eventName);
      }
    } catch (e) {
      console.warn('Pixel event error:', e);
    }
  }
}

